import { fetchWithAuth } from '../AuthService';
import { DATA_SERVICE_BASE_URL } from '../ApiService';

/**
 * API service for managing document-endeavor associations
 * Based on the Document Endeavor endpoints in the Postman collection
 */
export class DocumentEndeavorApi {
  
  /**
   * Associate a document with an endeavor
   * @param {number} documentId - The document ID
   * @param {number} endeavorId - The endeavor ID
   * @returns {Promise<Response>} - The fetch response
   */
  static async associateDocumentWithEndeavor(documentId, endeavorId) {
    console.log(`DocumentEndeavorApi: Associating document ${documentId} with endeavor ${endeavorId}`);
    
    return fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/documents/${documentId}/endeavors/${endeavorId}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      }
    });
  }

  /**
   * Remove a document from an endeavor
   * @param {number} documentId - The document ID
   * @param {number} endeavorId - The endeavor ID
   * @returns {Promise<Response>} - The fetch response
   */
  static async removeDocumentFromEndeavor(documentId, endeavorId) {
    console.log(`DocumentEndeavorApi: Removing document ${documentId} from endeavor ${endeavorId}`);
    
    return fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/documents/${documentId}/endeavors/${endeavorId}`, {
      method: 'DELETE'
    });
  }

  /**
   * Get all documents associated with a specific endeavor
   * @param {number} endeavorId - The endeavor ID
   * @returns {Promise<Response>} - The fetch response containing documents array
   */
  static async getDocumentsByEndeavor(endeavorId) {
    console.log(`DocumentEndeavorApi: Getting documents for endeavor ${endeavorId}`);
    
    return fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/endeavors/${endeavorId}/documents`, {
      method: 'GET'
    });
  }

  /**
   * Get all endeavors associated with a specific document
   * @param {number} documentId - The document ID
   * @returns {Promise<Response>} - The fetch response containing endeavors array
   */
  static async getEndeavorsForDocument(documentId) {
    console.log(`DocumentEndeavorApi: Getting endeavors for document ${documentId}`);
    
    return fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/documents/${documentId}/endeavors`, {
      method: 'GET'
    });
  }

  /**
   * ✅ UPDATED: Get existing endeavors for a document filtered by user
   * @param {number} documentId - The document ID
   * @param {number} userId - The user ID to filter by
   * @returns {Promise<Array>} - Array of endeavors that use this document for this user
   */
  static async getExistingEndeavorsForDocument(documentId, userId) {
    console.log(`DocumentEndeavorApi: Checking for existing endeavors for document ${documentId} and user ${userId}`);
    
    try {
      // Get all endeavors associated with this document
      const response = await this.getEndeavorsForDocument(documentId);
      
      if (!response.ok) {
        if (response.status === 404) {
          // No associations found
          return [];
        }
        const errorText = await response.text();
        throw new Error(`Failed to get endeavors for document: ${response.status} - ${errorText}`);
      }
      
      const endeavorIdsOrObjects = await response.json();
      console.log('Raw response from getEndeavorsForDocument:', endeavorIdsOrObjects);
      
      // ✅ NEW: Handle case where API returns just endeavor IDs
      if (Array.isArray(endeavorIdsOrObjects) && endeavorIdsOrObjects.length > 0) {
        // Check if we got just IDs or full objects
        const firstItem = endeavorIdsOrObjects[0];
        
        if (typeof firstItem === 'number' || (typeof firstItem === 'string' && !isNaN(firstItem))) {
          // ✅ We got just endeavor IDs, need to fetch full endeavor details
          console.log('Got endeavor IDs, fetching full endeavor details...');
          
          const endeavorDetails = [];
          
          for (const endeavorId of endeavorIdsOrObjects) {
            try {
              console.log(`Fetching details for endeavor ${endeavorId}...`);
              
              // Import EndeavorApi here to avoid circular imports
              const { EndeavorApi } = await import('../ApiService');
              const endeavorResponse = await EndeavorApi.getEndeavor(endeavorId);
              
              if (endeavorResponse.ok) {
                const endeavor = await endeavorResponse.json();
                
                // ✅ Filter by user ownership
                if (this.isUserOwnedEndeavor(endeavor, userId)) {
                  endeavorDetails.push(endeavor);
                  console.log(`✅ Found user-owned endeavor: ${endeavor.name} (ID: ${endeavor.endeavorId})`);
                } else {
                  console.log(`❌ Endeavor ${endeavor.endeavorId} not owned by user ${userId}`);
                }
              } else {
                console.warn(`Failed to fetch endeavor ${endeavorId}: ${endeavorResponse.status}`);
              }
            } catch (error) {
              console.warn(`Error fetching endeavor ${endeavorId}:`, error);
            }
          }
          
          console.log(`Found ${endeavorDetails.length} user-owned endeavors for document ${documentId}`);
          return endeavorDetails;
          
        } else {
          // ✅ We got full endeavor objects, filter by user
          console.log('Got full endeavor objects, filtering by user...');
          
          const userEndeavors = endeavorIdsOrObjects.filter(endeavor => 
            this.isUserOwnedEndeavor(endeavor, userId)
          );
          
          console.log(`Found ${userEndeavors.length} user-owned endeavors for document ${documentId}`);
          return userEndeavors;
        }
      }
      
      // Empty array or no endeavors found
      return [];
      
    } catch (error) {
      console.error('Error getting existing endeavors for document:', error);
      // Return empty array instead of throwing to avoid blocking the UI
      return [];
    }
  }

  /**
   * ✅ NEW: Helper method to check if an endeavor is owned by a user
   * @param {Object} endeavor - The endeavor object
   * @param {number} userId - The user ID to check
   * @returns {boolean} - True if the endeavor is owned by the user
   */
  static isUserOwnedEndeavor(endeavor, userId) {
    // Check various possible user ID fields in the endeavor object
    return (
      endeavor.userId === userId ||
      endeavor.createdBy === userId ||
      endeavor.ownerId === userId ||
      endeavor.user_id === userId ||
      endeavor.created_by === userId ||
      endeavor.owner_id === userId ||
      // ✅ Convert to numbers for comparison if needed
      parseInt(endeavor.userId) === parseInt(userId) ||
      parseInt(endeavor.createdBy) === parseInt(userId) ||
      parseInt(endeavor.ownerId) === parseInt(userId) ||
      parseInt(endeavor.user_id) === parseInt(userId) ||
      parseInt(endeavor.created_by) === parseInt(userId) ||
      parseInt(endeavor.owner_id) === parseInt(userId)
    );
  }

  /**
   * Associate multiple documents with an endeavor
   * @param {number[]} documentIds - Array of document IDs
   * @param {number} endeavorId - The endeavor ID
   * @returns {Promise<{successful: number[], failed: Array<{documentId: number, error: string}>}>}
   */
  static async associateMultipleDocumentsWithEndeavor(documentIds, endeavorId) {
    console.log(`DocumentEndeavorApi: Associating ${documentIds.length} documents with endeavor ${endeavorId}`);
    
    const results = {
      successful: [],
      failed: []
    };

    // Process each association sequentially to avoid overwhelming the server
    for (const documentId of documentIds) {
      try {
        const response = await this.associateDocumentWithEndeavor(documentId, endeavorId);
        
        if (response.ok) {
          results.successful.push(documentId);
        } else {
          const errorText = await response.text();
          results.failed.push({
            documentId,
            error: `${response.status}: ${errorText}`
          });
        }
      } catch (error) {
        results.failed.push({
          documentId,
          error: error.message
        });
      }
    }

    return results;
  }

  /**
   * Remove multiple documents from an endeavor
   * @param {number[]} documentIds - Array of document IDs
   * @param {number} endeavorId - The endeavor ID
   * @returns {Promise<{successful: number[], failed: Array<{documentId: number, error: string}>}>}
   */
  static async removeMultipleDocumentsFromEndeavor(documentIds, endeavorId) {
    console.log(`DocumentEndeavorApi: Removing ${documentIds.length} documents from endeavor ${endeavorId}`);
    
    const results = {
      successful: [],
      failed: []
    };

    for (const documentId of documentIds) {
      try {
        const response = await this.removeDocumentFromEndeavor(documentId, endeavorId);
        
        if (response.ok) {
          results.successful.push(documentId);
        } else {
          const errorText = await response.text();
          results.failed.push({
            documentId,
            error: `${response.status}: ${errorText}`
          });
        }
      } catch (error) {
        results.failed.push({
          documentId,
          error: error.message
        });
      }
    }

    return results;
  }

  /**
   * Check if a document is associated with an endeavor
   * @param {number} documentId - The document ID
   * @param {number} endeavorId - The endeavor ID
   * @returns {Promise<boolean>} - True if associated, false otherwise
   */
  static async isDocumentAssociatedWithEndeavor(documentId, endeavorId) {
    try {
      const response = await this.getEndeavorsForDocument(documentId);
      
      if (!response.ok) {
        return false;
      }
      
      const endeavors = await response.json();
      const endeavorsArray = Array.isArray(endeavors) ? endeavors : [endeavors];
      
      return endeavorsArray.some(endeavor => 
        (endeavor.endeavorId || endeavor.id) === endeavorId
      );
    } catch (error) {
      console.error(`Error checking document-endeavor association:`, error);
      return false;
    }
  }
}

export default DocumentEndeavorApi;